// Client entry (PLAN-C1.3 F1d). In production the app boots by loading the real session from the
// auth-worker (/auth/me); an unauthenticated user is redirected to AuthKit login before anything
// renders. In dev (import.meta.env.DEV) it uses the injected DEV_SESSION so local work needs no
// WorkOS round-trip. Shared TanStack Start SSR swap is the C4.0-D5 follow-up.
import "./index.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import { DEV_SESSION, setBootedSession } from "./lib/session";
import { loadSession, redirectToLogin, startSessionKeepAlive } from "./lib/auth";
import { AUTH_BASE, APP_ORIGIN } from "./lib/env";

// Bounce-guard: only redirect to AuthKit ONCE per attempt; if we return from login and still can't
// load a session, render a clear message instead of white-page-bouncing forever. Cleared on success.
const ATTEMPT_KEY = "sympllex_auth_attempt";

function renderAuthError(el: HTMLElement): void {
  el.innerHTML =
    '<div style="max-width:28rem;margin:18vh auto;font-family:Inter,system-ui,sans-serif;text-align:center;color:#1e293b">' +
    '<h1 style="font-size:1.125rem;font-weight:600;margin:0 0 .5rem">Kan de sessie niet laden</h1>' +
    '<p style="font-size:.875rem;color:#64748b;margin:0 0 1.25rem">Je bent ingelogd, maar je sessie kon niet worden geladen. Probeer opnieuw in te loggen.</p>' +
    '<a href="' + AUTH_BASE + '/auth/login" style="display:inline-block;background:#2563eb;color:#fff;font-size:.875rem;font-weight:500;padding:.5rem 1rem;border-radius:.5rem;text-decoration:none">Opnieuw inloggen</a>' +
    "</div>";
}

async function boot(): Promise<void> {
  const el = document.getElementById("root");
  if (!el) throw new Error("#root element not found");

  // Canonical host: forward legacy *.pages.dev URLs to the first-party app origin so auth stays
  // first-party in every browser.
  if (!import.meta.env.DEV && location.hostname.endsWith(".pages.dev")) {
    window.location.replace(`${APP_ORIGIN}${location.pathname}${location.search}${location.hash}`);
    return;
  }

  let session = DEV_SESSION;
  if (!import.meta.env.DEV) {
    const loaded = await loadSession();
    if (!loaded) {
      if (sessionStorage.getItem(ATTEMPT_KEY)) {
        sessionStorage.removeItem(ATTEMPT_KEY);
        renderAuthError(el);
        return;
      }
      sessionStorage.setItem(ATTEMPT_KEY, "1");
      redirectToLogin();
      return; // unauthenticated → AuthKit; do not render
    }
    sessionStorage.removeItem(ATTEMPT_KEY);
    session = loaded;
    startSessionKeepAlive(); // keep the 15-min access token fresh so requests never hit an expired boundary
  }
  setBootedSession(session);

  createRoot(el).render(
    <StrictMode>
      <RouterProvider router={router} context={{ session }} />
    </StrictMode>,
  );
}

void boot();
